import { useCallback, useEffect, useRef, useState } from "react";
import { GetPlayerLotRecentBids } from "../../redux/auth-reducer/auth-service";
import { readRecentBidsReply } from "../../utils/helper/recentBids";

/**
 * How long after the selected lot's figures change its bids are read again: one request for a
 * burst of pushes, as for the Auction Winnings block (useLotWinnings).
 */
export const RECENT_BIDS_REFETCH_DELAY_MS = 300;

const EMPTY = { teamId: null, bids: [], serverNowUtc: null, receivedAt: 0, loading: false };

/**
 * Recent Bids of the selected team (#239): its five newest bids, from Player/GetLotRecentBids.
 *
 * - Read when a team is selected, and the list is replaced at once: another team's rows are never
 *   shown under this one's name (AC12).
 * - Read again a moment after the selected lot's bid figures change (totalPlacedBids, topBidAmount,
 *   bidPlacedBy), whichever way they arrived: another player's bid pushed live, the player's own bid
 *   and the reload after it, the read after a reconnect (AC10). The lot itself is kept in step by
 *   useAuctionDetails, on the same pushes as the rest of the page.
 * - A team nobody has bid on shows the empty state without a request.
 *
 * Only the newest request's reply is written. No reply keeps the rows on screen; a refusal (the
 * auction is no longer the player's to see) reads as no bids.
 *
 * @param {number|string} auctionId The page's auction.
 * @param {object|null} lot The selected lot.
 * @returns {{bids: object[], serverNowUtc: (string|null), receivedAt: number, loading: boolean}}
 */
export const useRecentBids = (auctionId, lot) => {
  const [state, setState] = useState(EMPTY);
  // The number of the latest request; a reply to an earlier one is out of date.
  const requestRef = useRef(0);
  // The team the list belongs to, to tell a new selection from new figures of the same team.
  const teamRef = useRef(null);

  const teamId = lot?.teamId ?? null;
  const hasBids = Number(lot?.totalPlacedBids) > 0 || (lot?.topBidAmount !== null && lot?.topBidAmount !== undefined);
  const figures = lot ? `${lot.totalPlacedBids}|${lot.topBidAmount}|${lot.bidPlacedBy}` : "";

  const load = useCallback(
    async (forTeam) => {
      const request = ++requestRef.current;
      const reply = readRecentBidsReply(await GetPlayerLotRecentBids(auctionId, forTeam));
      if (request !== requestRef.current) return;
      if (!reply) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }
      setState({ teamId: forTeam, bids: reply.bids, serverNowUtc: reply.serverNowUtc, receivedAt: Date.now(), loading: false });
    },
    [auctionId]
  );

  useEffect(() => {
    if (teamId === null) return undefined;
    const switched = teamRef.current !== teamId;
    teamRef.current = teamId;

    if (!hasBids) {
      // A reply still on its way is for figures that no longer hold.
      requestRef.current += 1;
      setState({ ...EMPTY, teamId });
      return undefined;
    }
    if (switched) {
      setState({ ...EMPTY, teamId, loading: true });
      load(teamId);
      return undefined;
    }
    const timer = setTimeout(() => load(teamId), RECENT_BIDS_REFETCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [teamId, hasBids, figures, load]);

  // Leaving the page: no reply may be written any more.
  useEffect(() => {
    const requests = requestRef;
    return () => {
      requests.current += 1;
    };
  }, []);

  const current = state.teamId === teamId;
  return {
    bids: current ? state.bids : [],
    serverNowUtc: current ? state.serverNowUtc : null,
    receivedAt: current ? state.receivedAt : 0,
    loading: current ? state.loading : hasBids,
  };
};
